import isBoolean from 'lodash/isBoolean';
import isNil from 'lodash/isNil';
import isUndefined from 'lodash/isUndefined';
import React, {Component, Fragment} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import uuidV4 from 'uuid/v4';
import Alert from '@spothero/ui/alert';
import Form from '@spothero/ui/form';
import FormGroupError from '@spothero/ui/form-group-error';
import TextButton from '@spothero/ui/text-button';
import AnimationUtils from '@spothero/utils/animation';
import DOMUtils from '@spothero/utils/dom';
import EnvironmentUtils from '@spothero/utils/environment';
import UserUtils from 'user/user-utils';
import CreditCardSelect from 'user/card/CreditCardSelect';
import PromocodeControl from 'user/promo/PromocodeControl';
import {updateData} from '../checkout-actions';
import CheckoutUtils from '../checkout-utils';
import CheckoutPanel from '../common/CheckoutPanel';
import PurchaseButton from './PurchaseButton';
import ReviewPurchase from '../summary/ReviewPurchase';

class PaymentInformation extends Component {
    static propTypes = {
        isFirstView: PropTypes.bool.isRequired,
        user: PropTypes.object.isRequired,
        isMonthly: PropTypes.bool,
        hourlyRates: PropTypes.arrayOf(PropTypes.object),
        monthlyRates: PropTypes.arrayOf(PropTypes.object),
        spotId: PropTypes.number,
        selectedRate: PropTypes.object,
        selectedCreditCard: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number
        ]),
        paymentRequired: PropTypes.bool,
        paymentRequiredNoCharge: PropTypes.bool,
        isNewCreditCard: PropTypes.bool,
        purchaseOnBehalfOfCustomer: PropTypes.bool,
        purchaseForCustomer: PropTypes.bool,
        promoCode: PropTypes.object,
        incomingPromoCode: PropTypes.string,
        checkoutError: PropTypes.shape({
            title: PropTypes.string,
            subtitle: PropTypes.string,
            errors: PropTypes.arrayOf(PropTypes.string)
        }),
        reservationDetailsEdit: PropTypes.bool.isRequired,
        isPurchasing: PropTypes.bool.isRequired,
        isRebookingReservation: PropTypes.bool,
        onReserveClick: PropTypes.func.isRequired,
        onUpdateData: PropTypes.func.isRequired
    };

    constructor(props) {
        super(props);

        const {
            user,
            selectedCreditCard
        } = props;

        this._isMobile = EnvironmentUtils.isMobileDevice();
        this._creditExceedsPrice = false;
        this._showPaymentMethods = true;
        this._isPaymentRequired = true;

        this.state = {
            prevUser: user,
            prevSelectedCard: selectedCreditCard,
            isUserAddingNewCard: (selectedCreditCard === 'new'),
            creditCardSelectKey: uuidV4()
        };
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        const {
            user,
            selectedCreditCard
        } = nextProps;
        const {
            prevUser,
            prevSelectedCard
        } = prevState;

        if (user.status === UserUtils.AUTH_STATE.LOGGED_OUT
            || user.email !== prevUser.email
            || selectedCreditCard !== prevSelectedCard
        ) {
            return {
                prevUser: user,
                prevSelectedCard: selectedCreditCard,
                creditCardSelectKey: uuidV4()
            };
        }

        return null;
    }

    componentDidUpdate(prevProps, prevState) {
        const {
            onUpdateData
        } = this.props;
        const {
            isUserAddingNewCard
        } = this.state;
        const {
            paymentRequired,
            isNewCreditCard
        } = prevProps;

        if (paymentRequired !== this._isPaymentRequired) {
            onUpdateData({
                paymentRequired: this._isPaymentRequired
            });
        } else if (isNewCreditCard !== isUserAddingNewCard) {
            // this is used to update the panel preview for payment info
            onUpdateData({
                isNewCreditCard: isUserAddingNewCard
            });
        }
    }

    _onOpen() {
        setTimeout(() => {
            AnimationUtils.scrollTo('html', DOMUtils.getDocumentOffset('.CheckoutPanel-reservation').top);
        }, 500);
    }

    _onRemovePromoClick = evt => {
        evt.preventDefault();

        this.props.onUpdateData({
            paymentRequired: true,
            paymentRequiredNoCharge: false,
            promoCode: null,
            incomingPromoCode: null
        });
    }

    _onPromoAdded = (promoCode, data) => {
        const {
            selectedRate,
            purchaseOnBehalfOfCustomer,
            onUpdateData
        } = this.props;
        const {
            percentageOff,
            percentageOffDiscountMax,
            creditAmount,
            discountAmount
        } = this._getPromoCodeData(data);
        const discountDisplayAmount = `$${discountAmount}`;
        let paymentRequired = true;
        let paymentRequiredNoCharge = false;
        let promoCodeDetails;

        if ((selectedRate.price - data.discount_amount) <= 0) {
            if (!purchaseOnBehalfOfCustomer) {
                paymentRequiredNoCharge = true;
            } else {
                paymentRequired = false;
            }
        }

        if (percentageOff) {
            promoCodeDetails = `${percentageOff}% off`;

            if (percentageOffDiscountMax) {
                promoCodeDetails += ` up to $${percentageOffDiscountMax}`;
            }
        } else {
            promoCodeDetails = discountDisplayAmount.toString();
        }

        onUpdateData({
            paymentRequired,
            paymentRequiredNoCharge,
            promoCode: {
                code: promoCode.toUpperCase(),
                codeType: data.code_type,
                creditAmount,
                discountAmount,
                discountDisplayAmount,
                promoCodeDetails,
                percentageOff
            },
            incomingPromoCode: null
        });
    }

    _onCancelPromoCode = () => {
        this.props.onUpdateData({
            promoCode: null
        });
    }

    _onCardSelected = value => {
        this.setState({
            isUserAddingNewCard: (value === 'new')
        });
    }

    _getPromoCodeData(data) {
        return {
            percentageOff: (!isUndefined(data.percentage_off))
                ? data.percentage_off
                : 0,
            percentageOffDiscountMax: (!isUndefined(data.percentage_off_discount_max))
                ? (data.percentage_off_discount_max / 100)
                : 0,
            creditAmount: (!isUndefined(data.credit_amount))
                ? (data.credit_amount / 100)
                : 0,
            discountAmount: (data.discount_amount / 100)
        };
    }

    _renderCreditCardRequired() {
        const {
            paymentRequiredNoCharge,
            purchaseOnBehalfOfCustomer
        } = this.props;
        const showRequiredAlert = ((paymentRequiredNoCharge || this._creditExceedsPrice) && !purchaseOnBehalfOfCustomer);

        if (!this._isPaymentRequired) { return null; }

        return (
            <div className="payment-required-content">
                {showRequiredAlert &&
                    <Alert type="highlight">
                        A credit card is required to guarantee your reservation. It will not be charged.
                    </Alert>
                }
            </div>
        );
    }

    _renderPaymentRequired() {
        const {
            user,
            selectedCreditCard
        } = this.props;
        const {
            creditCardSelectKey
        } = this.state;

        return (
            <Fragment>
                {this._renderCreditCardRequired()}
                {this._showPaymentMethods &&
                    <CreditCardSelect
                        key={creditCardSelectKey}
                        ref={node => { this._creditCardSelect = node; }}
                        user={user}
                        selectedCard={selectedCreditCard}
                        onCardSelected={this._onCardSelected}
                    />
                }
            </Fragment>
        );
    }

    _renderPromoApplied() {
        const {
            code,
            promoCodeDetails
        } = this.props.promoCode;

        return (
            <div className="PaymentInformation-promo-applied">
                Promo Code: {code}
                {promoCodeDetails &&
                    <Fragment>
                        {' ('}
                        <span className="discount-amount">{promoCodeDetails}</span>
                        {')'}
                    </Fragment>
                }
                <TextButton onClick={this._onRemovePromoClick}>Remove Promo Code</TextButton>
            </div>
        );
    }

    _renderPromoEntry() {
        const {
            spotId,
            selectedRate,
            user,
            incomingPromoCode
        } = this.props;
        let value = '';
        let active = false;

        // is user is logged in and incomingPromoCode exists, submit it
        if (incomingPromoCode) {
            value = incomingPromoCode;
            active = true;
        }

        return (
            <Form
                ref={node => { this._promoCode = node; }}
                id="PaymentInformation-promo-code-form"
            >
                <PromocodeControl
                    placeholder={(this._isMobile) ? 'Promo Code' : ''}
                    spotId={spotId}
                    rate={selectedRate}
                    value={value}
                    active={active}
                    userEmail={user.email}
                    verifyInline
                    enterSubmits
                    onPromoAdded={this._onPromoAdded}
                    onCancelPromoCode={this._onCancelPromoCode}
                />
            </Form>
        );
    }

    _renderNotPurchaseForCustomer() {
        const {
            user,
            promoCode,
            paymentRequiredNoCharge
        } = this.props;
        const userHasCredit = (user.credit && (!promoCode || (this._isPaymentRequired && !paymentRequiredNoCharge)));

        return (
            <Fragment>
                {this._renderPaymentRequired()}
                {Boolean(userHasCredit) &&
                    <p>Available SpotHero Credit: ${(user.credit / 100)}</p>
                }
                {(promoCode && !isNaN(promoCode.discountAmount))
                    ? this._renderPromoApplied()
                    : this._renderPromoEntry()
                }
            </Fragment>
        );
    }

    submit() {
        const {
            user,
            selectedCreditCard,
            purchaseOnBehalfOfCustomer,
            purchaseForCustomer,
            promoCode
        } = this.props;
        let selectedCard = selectedCreditCard;
        let reservationData = false;
        let listValues;
        let promoValues;

        if (this._creditCardSelect) {
            listValues = this._creditCardSelect.submit();

            if (selectedCard !== 'new') {
                selectedCard = listValues.selectedCreditCard;
            }
        }

        if (this._promoCode) {
            promoValues = this._promoCode.submit();

            if (!promoValues) { return false; }
        }

        if (listValues && !isBoolean(listValues)) {
            if (listValues.selectedCreditCard !== 'new') {
                // if the credit card list submission passes and the selection is not a new credit card
                // we go ahead and set selectedCreditCard to the selected value as an integer
                reservationData = {
                    selectedCreditCard: parseInt(listValues.selectedCreditCard, 10)
                };
            } else {
                // if it is a new card we pull the data from the new card form and set that accordingly
                const {
                    fullName,
                    saveCreditCardAsDefault
                } = listValues;

                // a new card is being added
                reservationData = {
                    selectedCreditCard: 'new',
                    newCardInfo: {
                        fullName,
                        isDefault: saveCreditCardAsDefault
                    }
                };
            }
        }

        if (!reservationData && selectedCard && selectedCard !== 'new') {
            reservationData = {
                selectedCreditCard: selectedCard
            };
        }

        if (isBoolean(promoValues) && !promoValues) {
            return false;
        } else if (isNil(promoCode) && promoValues && promoValues.promoCode) {
            // if the promoCode wasn't already applied by clicking apply it won't be defined on the reservation data so add it
            const newPromoCode = {
                code: promoValues.promoCode
            };

            if (reservationData) {
                reservationData.promoCode = newPromoCode;
            } else {
                reservationData = {
                    promoCode: newPromoCode
                };
            }
        }

        if ((this._creditExceedsPrice && user.isAdmin && purchaseOnBehalfOfCustomer) || purchaseForCustomer) {
            reservationData = {
                selectedCreditCard: 'admin'
            };
        }

        return reservationData;
    }

    render() {
        const {
            user: {
                credit,
                isAdmin,
                isCheckoutCTAs,
                status,
            },
            isFirstView,
            isMonthly,
            hourlyRates,
            monthlyRates,
            selectedRate,
            promoCode,
            paymentRequired,
            purchaseForCustomer,
            purchaseOnBehalfOfCustomer,
            reservationDetailsEdit,
            isPurchasing,
            isRebookingReservation,
            checkoutError,
            onReserveClick
        } = this.props;
        const hasMultipleRates = ((selectedRate && (!isMonthly && hourlyRates && hourlyRates.length > 1)) || (isMonthly && monthlyRates && monthlyRates.length > 1));
        const isOpen = Boolean((selectedRate && status !== UserUtils.AUTH_STATE.LOGGED_OUT && !reservationDetailsEdit && (!isFirstView || !hasMultipleRates)));
        const {
            amountToCharge
        } = CheckoutUtils.getAmountToCharge({
            price: (selectedRate) ? selectedRate.price : 0,
            discount: (promoCode) ? (promoCode.discountAmount * 100) : 0, // TODO bk - should come to us in pennies
            userCredit: credit
        });
        const showPurchaseButtonAdminBorder = (isAdmin && !this._isMobile && (purchaseForCustomer || purchaseOnBehalfOfCustomer || isRebookingReservation));
        let discountedPrice = (selectedRate) ? (selectedRate.price / 100) : 0;

        this._creditExceedsPrice = false;
        this._showPaymentMethods = true;
        this._isPaymentRequired = paymentRequired;

        if (promoCode) {
            discountedPrice -= promoCode.discountAmount;

            if (discountedPrice <= 0) {
                this._creditExceedsPrice = true;
            }
        }

        // make sure the discountedPrice is at least 0
        discountedPrice = (discountedPrice > 0) ? discountedPrice : 0;

        if (status === UserUtils.AUTH_STATE.USER && (credit / 100) >= discountedPrice) {
            this._creditExceedsPrice = true;
        }

        // check if the user has credit cards and if they have enough credit to pay for the whole reservation as well
        // as if the rate is not a monthly recurrable rate in which case we don't have to show the payment methods
        if ((this._creditExceedsPrice && isAdmin && purchaseOnBehalfOfCustomer) || purchaseForCustomer) {
            this._showPaymentMethods = false;
        }

        return (
            <CheckoutPanel
                className="CheckoutPanel-payment"
                title={(
                    <Fragment>
                        <em>3</em> Payment Details
                    </Fragment>
                )}
                open={isOpen}
                onOpen={this._onOpen}
            >
                {selectedRate &&
                    <div className="PaymentInformation">
                        {!(purchaseForCustomer || isRebookingReservation) &&
                            this._renderNotPurchaseForCustomer()
                        }
                        {(purchaseForCustomer || isRebookingReservation) &&
                            <div className="well well-sm">
                                <p>Purchasing for customer, no payment required.</p>
                            </div>
                        }
                        <ReviewPurchase />
                    </div>
                }
                <PurchaseButton
                    price={amountToCharge}
                    showPrice={false}
                    loading={isPurchasing}
                    disabled={(isPurchasing || !selectedRate)}
                    onReserveClick={onReserveClick}
                    showAdminBorder={showPurchaseButtonAdminBorder}
                    isCTAVariation={isCheckoutCTAs}
                />
                {checkoutError &&
                    <FormGroupError>
                        {checkoutError.subtitle &&
                            <h1 className="heading-sm">{checkoutError.subtitle}</h1>
                        }
                        {checkoutError.errors[0]}
                    </FormGroupError>
                }
            </CheckoutPanel>
        );
    }
}

const mapStateToProps = state => {
    const {
        checkout: {
            selectedCreditCard,
            paymentRequired,
            paymentRequiredNoCharge,
            isNewCreditCard,
            purchaseOnBehalfOfCustomer,
            purchaseForCustomer,
            promoCode,
            incomingPromoCode,
            reservationDetailsEdit,
            isPurchasing,
            isRebookingReservation,
            checkoutError
        },
        searchRequest: {
            monthly
        },
        spot: {
            selected: {
                spotId,
                selectedRate,
                houlry_rates: hourlyRates,
                monthly_rates: monthlyRates
            }
        },
        user: {
            data: userData
        }
    } = state;

    return {
        user: userData,
        isMonthly: monthly,
        hourlyRates,
        monthlyRates,
        spotId,
        selectedRate,
        selectedCreditCard,
        paymentRequired,
        paymentRequiredNoCharge,
        isNewCreditCard,
        purchaseOnBehalfOfCustomer,
        purchaseForCustomer,
        promoCode,
        incomingPromoCode,
        reservationDetailsEdit,
        isPurchasing,
        isRebookingReservation,
        checkoutError
    };
};

const mapDispatchToProps = {
    onUpdateData: updateData
};

export default connect(mapStateToProps, mapDispatchToProps, null, {forwardRef: true})(PaymentInformation);
